/**
 * Session-local weekly fuel price history (Control Centre Fuel chart + table).
 * Inferred rows are UI forward-fill only — not ZeeCore truth.
 */

import { shiftYmd } from '../lib/calc';

export type FuelPriceSource = 'pdf' | 'manual' | 'inferred';

export type FuelPriceWeekEntry = {
  weekOf: string; // YYYY-MM-DD (week commencing)
  pencePerLitre: number;
  source: FuelPriceSource;
  matchedLine?: string;
  filename?: string;
};

/** Keep at most one preview week (latest confirmed + 7 days). */
export function trimPriceHistoryPreview(entries: FuelPriceWeekEntry[]): FuelPriceWeekEntry[] {
  const confirmed = entries.filter((e) => e.source !== 'inferred');
  if (confirmed.length === 0) {
    return [...entries]
      .filter((e) => e.source !== 'inferred')
      .sort((a, b) => b.weekOf.localeCompare(a.weekOf));
  }
  const latest = [...confirmed].sort((a, b) => b.weekOf.localeCompare(a.weekOf))[0];
  const previewWeek = shiftYmd(latest.weekOf, 7);
  return entries
    .filter((e) => e.source !== 'inferred' || e.weekOf === previewWeek)
    .sort((a, b) => b.weekOf.localeCompare(a.weekOf));
}

/** Merge a confirmed save into history and forward-fill one inferred week (session preview). */
export function upsertPriceHistory(
  existing: FuelPriceWeekEntry[],
  entry: FuelPriceWeekEntry,
): FuelPriceWeekEntry[] {
  const confirmed = existing
    .filter((e) => e.source !== 'inferred' && e.weekOf !== entry.weekOf)
    .concat([{ ...entry, source: entry.source === 'inferred' ? 'manual' : entry.source }]);

  confirmed.sort((a, b) => b.weekOf.localeCompare(a.weekOf));

  const latest = confirmed[0];
  if (!latest) return confirmed;

  const withInferred = [...confirmed];
  const weekOf = shiftYmd(latest.weekOf, 7);
  if (!withInferred.some((e) => e.weekOf === weekOf && e.source !== 'inferred')) {
    withInferred.push({
      weekOf,
      pencePerLitre: latest.pencePerLitre,
      source: 'inferred',
    });
  }

  return trimPriceHistoryPreview(withInferred);
}
